import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { FileModal, ViewFiledModal } from '../components/ComplianceModals';
import { IconAlertTriangle, IconShieldCheck, IconCalendar } from '../components/icons';
import { useCompany } from '../context/CompanyContext';
import { fyLabel } from '../utils/fy';
import api from '../api';
import './Dashboard.css';

// Every category's bar color is computed from its own fill state (green
// once fully filed, red otherwise) rather than a fixed per-category color -
// TDS/GST/PF/ESIC used to be hardcoded to a single color regardless of
// actual completion, so e.g. TDS could show 8/8 filed (100%) in amber.
const CATEGORY_META = {
  GST: { label: 'GST Returns', color: 'red-if-incomplete' },
  TDS: { label: 'TDS / TCS', color: 'red-if-incomplete' },
  ROC: { label: 'ROC / MCA', color: 'red-if-incomplete' },
  PF: { label: 'PF', color: 'red-if-incomplete' },
  ESIC: { label: 'ESIC', color: 'red-if-incomplete' },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  return Math.round((due - today) / 86400000);
}

function inr(amount) {
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

function Dashboard() {
  const { selectedCompanyId, selectedCompany, fyStartYear } = useCompany();
  const location = useLocation();

  const [dashboard, setDashboard] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filingBusyId, setFilingBusyId] = useState(null);
  const [fileModalItem, setFileModalItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [toast, setToast] = useState(location.state?.toast || null);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const loadCompanyData = (companyId, fyYear) => {
    setLoading(true);
    Promise.all([
      api.get(`/dashboard/${companyId}`, { params: { fy_year: fyYear } }),
      api.get(`/compliance-items/${companyId}`, { params: { fy_year: fyYear } }),
    ])
      .then(([dashRes, itemsRes]) => {
        setDashboard(dashRes.data);
        setAllItems(itemsRes.data);
        setActionItems([...dashRes.data.overdue_items, ...dashRes.data.upcoming_7_days]);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (selectedCompanyId) loadCompanyData(selectedCompanyId, fyStartYear);
  }, [selectedCompanyId, fyStartYear]);

  const confirmFileNow = (payload) => {
    if (!fileModalItem) return undefined;
    const itemId = fileModalItem.id;
    setFilingBusyId(itemId);
    return api
      .post(`/compliance/${itemId}/file`, payload)
      .then((res) => {
        setActionItems((prev) =>
          prev.map((it) => (it.id === itemId ? { ...it, ...res.data } : it))
        );
        setFileModalItem(null);
        Promise.all([
          api.get(`/dashboard/${selectedCompanyId}`, { params: { fy_year: fyStartYear } }),
          api.get(`/compliance-items/${selectedCompanyId}`, { params: { fy_year: fyStartYear } }),
        ]).then(([dashRes, itemsRes]) => {
          setDashboard(dashRes.data);
          setAllItems(itemsRes.data);
        });
      })
      .finally(() => setFilingBusyId(null));
  };

  if (error) {
    return (
      <PageLayout>
        <p className="dash-error">Backend unreachable: {error}</p>
      </PageLayout>
    );
  }

  if (loading && !dashboard) {
    return (
      <PageLayout>
        <div className="dash-spinner-wrap">
          <div className="dash-spinner" />
          <p>Loading dashboard...</p>
        </div>
      </PageLayout>
    );
  }

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  const itemsThisMonth = allItems.filter(
    (it) => it.period_month === currentMonth && it.period_year === currentYear && it.is_applicable !== false
  );
  const filedThisMonth = itemsThisMonth.filter((it) => it.status === 'filed').length;

  const nextDueItem = allItems
    .filter((it) => it.status === 'pending' && it.is_applicable !== false)
    .sort((a, b) => (a.due_date < b.due_date ? -1 : 1))[0];

  const sortedActionItems = [...actionItems].sort((a, b) =>
    a.due_date < b.due_date ? -1 : 1
  );

  return (
    <PageLayout>
      {toast && <div className="cal-toast">{toast}</div>}

      <div className="page-title-row">
        {location.state?.fromConsolidated && (
          <p className="dash-breadcrumb">
            <Link to="/dashboard/consolidated">Consolidated View</Link> &gt; {selectedCompany?.name || '...'} &gt; Dashboard
          </p>
        )}
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">{fyLabel(dashboard.fy_year)}</p>
      </div>

      <div className={`dash-compact ${loading ? 'dash-refreshing' : ''}`}>
        {dashboard.overdue_count > 0 && (
          <div className="dash-alert-banner">
            ⚠ {dashboard.overdue_count} filings overdue — {inr(dashboard.penalty_exposure)} penalty
            exposure if not filed today
          </div>
        )}

        <div className="dash-stat-grid">
          <div className="dash-stat-card">
            <div className={`dash-stat-icon ${dashboard.penalty_exposure > 0 ? 'red' : 'grey'}`}>
              <IconAlertTriangle />
            </div>
            <div
              className={`dash-stat-value dash-serif ${
                dashboard.penalty_exposure > 0 ? 'red' : 'grey'
              }`}
            >
              {inr(dashboard.penalty_exposure)}
            </div>
            <div className="dash-stat-label">Penalty Exposure</div>
            <div className="dash-stat-sub">If not filed today</div>
          </div>

          <div className="dash-stat-card">
            <div className="dash-stat-icon green"><IconShieldCheck /></div>
            <div className="dash-stat-value dash-serif green">{dashboard.compliance_score}%</div>
            <div className="dash-stat-label">Compliance Score</div>
            <div className="dash-stat-sub">{fyLabel(dashboard.fy_year)}</div>
          </div>

          <div className="dash-stat-card">
            <div className="dash-stat-icon teal"><IconCalendar /></div>
            <div className="dash-stat-value dash-serif">
              {filedThisMonth} / {itemsThisMonth.length}
            </div>
            <div className="dash-stat-label">Filings This Month</div>
            <div className="dash-stat-sub">{MONTH_NAMES[today.getMonth()]}</div>
          </div>

          <div className="dash-stat-card">
            {nextDueItem ? (
              <>
                <div className={`dash-stat-icon ${daysUntil(nextDueItem.due_date) <= 7 ? 'amber' : 'green'}`}>
                  <IconCalendar />
                </div>
                <div
                  className={`dash-stat-value dash-serif ${
                    daysUntil(nextDueItem.due_date) <= 7 ? 'amber' : 'green'
                  }`}
                >
                  {formatDate(nextDueItem.due_date)}
                </div>
                <div className="dash-stat-label">Next Due Date</div>
                <div className="dash-stat-sub">{nextDueItem.form_name}</div>
              </>
            ) : (
              <>
                <div className="dash-stat-icon grey"><IconCalendar /></div>
                <div className="dash-stat-value dash-serif grey">—</div>
                <div className="dash-stat-label">Next Due Date</div>
                <div className="dash-stat-sub">Nothing pending</div>
              </>
            )}
          </div>
        </div>

        <div className="dash-card">
          <h2 className="dash-card-title">Action required</h2>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Filing Name</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Penalty/day</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedActionItems.length === 0 && (
                <tr>
                  <td colSpan={5} className="dash-empty">
                    Nothing needs action right now.
                  </td>
                </tr>
              )}
              {sortedActionItems.map((item) => {
                const days = daysUntil(item.due_date);
                return (
                  <tr key={item.id}>
                    <td>
                      {item.form_name}
                      <div className="dash-table-sub">{item.category}</div>
                    </td>
                    <td className={item.status === 'overdue' ? 'red' : item.status === 'filed' ? '' : 'amber'}>
                      {formatDate(item.due_date)}
                    </td>
                    <td>
                      {item.status === 'overdue' && (
                        <span className="dash-pill dash-pill-red">Overdue</span>
                      )}
                      {item.status === 'pending' && (
                        <span className="dash-pill dash-pill-amber">
                          {days <= 0 ? 'Due today' : `${days} days`}
                        </span>
                      )}
                      {item.status === 'in_progress' && (
                        <span className="dash-pill dash-pill-inprogress">In Progress</span>
                      )}
                      {item.status === 'filed' && (
                        <span className="dash-pill dash-pill-green">Filed ✓</span>
                      )}
                    </td>
                    <td className="dash-serif">{inr(item.penalty_per_day)}</td>
                    <td>
                      {item.status === 'filed' ? (
                        <button
                          className="dash-btn dash-btn-green"
                          type="button"
                          onClick={() => setViewItem(item)}
                        >
                          View
                        </button>
                      ) : (
                        <button
                          className="dash-btn dash-btn-orange"
                          type="button"
                          disabled={filingBusyId === item.id}
                          onClick={() => setFileModalItem(item)}
                        >
                          {filingBusyId === item.id ? 'Filing...' : 'File Now'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {dashboard.overdue_audit_items.length > 0 && (
          <div className="dash-card">
            <h2 className="dash-card-title">Overdue Audits &amp; Recommendations</h2>
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Due Date</th>
                  <th>Risk</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.overdue_audit_items.map((item) => (
                  <tr key={`${item.kind}-${item.id}`}>
                    <td>
                      <Link to="/audit-status">{item.title}</Link>
                      <div className="dash-table-sub">{item.kind === 'occurrence' ? 'Audit' : 'Recommendation'}</div>
                    </td>
                    <td className="red">{formatDate(item.due_date)}</td>
                    <td>
                      {item.risk_rating && (
                        <span className={`dash-pill ${item.risk_rating === 'High' ? 'dash-pill-red' : item.risk_rating === 'Med' ? 'dash-pill-amber' : 'dash-pill-blue'}`}>
                          {item.risk_rating}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="dash-card">
          <h2 className="dash-card-title">Filing status overview</h2>
          {dashboard.category_breakdown.map((row) => {
            const meta = CATEGORY_META[row.category] || { label: row.category, color: 'green' };
            let barColor = meta.color;
            if (barColor === 'red-if-incomplete') {
              barColor = row.filed < row.total ? 'red' : 'green';
            }

            return (
              <div className="dash-progress-row" key={row.category}>
                <div className="dash-progress-label">
                  {meta.label}: {row.filed}/{row.total} filed
                </div>
                <div className="dash-progress-track">
                  <div
                    className={`dash-progress-fill dash-progress-${barColor}`}
                    style={{ width: `${row.pct}%` }}
                  />
                </div>
                <div className="dash-progress-pct">{row.pct}%</div>
              </div>
            );
          })}
        </div>
      </div>

      <ViewFiledModal item={viewItem} onClose={() => setViewItem(null)} />
      <FileModal
        item={fileModalItem}
        onClose={() => setFileModalItem(null)}
        onConfirm={confirmFileNow}
        busy={!!fileModalItem && filingBusyId === fileModalItem.id}
      />
    </PageLayout>
  );
}

export default Dashboard;
